import { gsap } from "gsap";
import { ResultCode } from "../../api/common/controller/net/constant/Constants";
import { ErrorCodesHandler, } from "../../loader/errorCodes/ErrorCodesHandler";
import { AppEvent, GameErrorEvent } from "../Constants";
import { AbstractController } from "./AbstractController";
/**
 * AbstractGameController.ts
 */
export class AbstractGameController extends AbstractController {
    constructor(factory) {
        super();
        this.createFactory(factory);
        this.run();
    }
    createFactory(factory) {
        this.baseFactory = factory;
    }
    /**
     * Uses `this.baseFactory`.
     */
    createModel() {
        this.baseModel = this.baseFactory.getModel();
        this.error = this.baseModel.getErrorData();
        this.operatorConfig = this.baseFactory.getOperatorConfig();
        this.responsibleGaming = this.baseModel.getResponsibleGaming();
    }
    /**
     * Register an event listener with the `game` event bus.
     * Only one function can be bound to an event per controller.
     *
     * Call `unregisterGame(type)` to remove the listener.
     */
    registerGame(event, action) {
        this.baseModel.register(event, action, this);
    }
    /**
     * Unregister an event listener from the `game` event bus.
     */
    unregisterGame(event) {
        this.baseModel.unregister(event, this);
    }
    /**
     * Dispatch an event to the `game` event bus.
     *
     * @param event - The event to dispatch.
     * @param delay - Optional delay in milliseconds before dispatching the event.
     */
    dispatchGame(event, delay = 0) {
        if (delay > 0) {
            gsap.delayedCall(delay / 1000, () => {
                this.baseModel.dispatch(event);
            });
        }
        else {
            this.baseModel.dispatch(event);
        }
    }
    /**
     * Awaits all async event listeners for the given `event`.
     *
     * Most event listeners do not return promises so they resolve instantly.
     *
     * @param event - The event to dispatch.
     */
    async dispatchBlockingEvent(event, delay = 0) {
        if (delay > 0) {
            await gsap.delayedCall(delay / 1000, () => { });
        }
        await this.baseModel.dispatchBlockingEvent(event);
    }
    handleErrorResponse(code, delay = 0) {
        this.baseModel.setRequestPermitted(true);
        this.error.reset();
        this.error.setCode(code);
        if (!code) {
            this.dispatchCore(GameErrorEvent.CONNECTION_ERROR, delay);
            // Doesn't this also fire AppEvent.ERROR?
        }
        if (code != ResultCode.INVALID_TOKEN &&
            code != ResultCode.OPERATOR_ERROR) {
            let event = AppEvent.ERROR;
            if (code == ResultCode.INSUFFICIENT_FUNDS ||
                code == ResultCode.INSUFFICIENT_BALANCE) {
                event = GameErrorEvent.INSUFFICIENT_FUNDS; //weird popup, 3 different
            }
            else if (code === ResultCode.ACCOUNT_LIMIT_EXCEEDED) {
                event = GameErrorEvent.ACCOUNT_LIMIT_EXCEEDED; //done
            }
            else if (code == ResultCode.REGULATORY_MESSAGE) {
                event = GameErrorEvent.REGULATORY_MESSAGE; //no popup
            }
            else if (code == ResultCode.WAGER_BONUS_MESSAGE) {
                event = GameErrorEvent.WAGER_BONUS; //weird popup
            }
            else if (code == ResultCode.WAGER_CASH_MESSAGE) {
                event = GameErrorEvent.WAGER_CASH; //weird popup
            }
            else if (code == ResultCode.RESPONSIBLE_GAMING_TIME_LIMIT_MESSAGE) {
                event = GameErrorEvent.RESPONSIBLE_GAMING_TIME_LIMIT; //done
            }
            else if (code == ResultCode.UNKNOWN_PROMOTION_CODE) {
                event = GameErrorEvent.UNKNOWN_PROMOTION_CODE; //done
            }
            else if (code == ResultCode.USER_NOT_AUTHENTICATED) {
                event = GameErrorEvent.USER_NOT_AUTHENTICATED; //weid popup
            }
            else if (code == ResultCode.GAME_ACCESS_DENIED) {
                event = GameErrorEvent.GAME_ACCESS_DENIED; //done
            }
            else if (code == ResultCode.JACKPOT_ALREADY_WON) {
                event = GameErrorEvent.JACKPOT_ALREADY_WON; //no popup
            }
            else if (code == ResultCode.ACCOUNT_LIMIT_EXCEEDED_GENERIC) {
                event = GameErrorEvent.ACCOUNT_LIMIT_EXCEEDED_GENERIC; //done
            }
            else if (code == ResultCode.RESPONSIBLE_GAMING_TURNOVER_LIMIT) {
                event = GameErrorEvent.RESPONSIBLE_GAMING_TURNOVER_LIMIT; //done
            }
            else if (code == ResultCode.RESTRICTED_TERRITORY) {
                event = GameErrorEvent.RESTRICTED_TERRITORY; //done
            }
            //if the event is still the generic one
            if (event == AppEvent.ERROR) {
                let errorCodes = ErrorCodesHandler.getInstance().getErrorCodes();
                let dialogData = errorCodes.get(code);
                if (dialogData && dialogData.display) {
                    this.dispatchCore(event, delay); // dispatch the generic error event before it is replaced by the dynamic popup event
                    event = AppEvent.DYNAMIC_POPUP;
                    event.setPayload(dialogData.display);
                }
            }
            this.dispatchCore(event, delay);
        }
    }
    isSuccess(code) {
        let success = false;
        if (code == ResultCode.SUCCESS) {
            this.baseModel.setRequestPermitted(true);
            this.error.reset();
            success = true;
        }
        return success;
    }
    isRequestPermitted() {
        let permitted = false;
        if (this.baseModel.isRequestPermitted()) {
            this.baseModel.setRequestPermitted(false);
            permitted = true;
        }
        return permitted;
    }
    getCurrentStake() {
        const frData = this.baseModel.getFreeRoundsData();
        return frData.isFreeRoundsEnabled()
            ? frData.getStake()
            : this.baseModel.getCurrentStake();
    }
    getFrId() {
        const frData = this.baseModel.getFreeRoundsData();
        return frData.isFreeRoundsEnabled() ? frData.getUUID() : null;
    }
}
//# sourceMappingURL=AbstractGameController.js.map